import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardBody, CardTitle, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  AddShiftAPI, DeleteClosureAPI, DeleteShiftAPI, EndPauseAPI, GetBookingSettingsAPI, GetClosuresAPI, GetShiftsAPI,
  SaveBookingSettingsAPI, SaveShiftAPI,
} from "network/bookings";
import { errText } from "./errText";
import HoursEditor, { ConfirmDelete } from "./HoursEditor";
import PauseModal from "./PauseModal";
import SpecialDayModal from "./SpecialDayModal";
import PhonePreview from "./settingsPreview";
import { CoverCard, OtherCard, RulesCard } from "./settingsSections";
import { bandName, clientBlock, closedDays, closureDates, dayStr, outsideOpening, sortClosures, sortShifts, untilLabel } from "./settingsLogic";

// Schede di Settings (a sinistra) e anteprima del cliente (a destra). `?tab=cover` apre direttamente una scheda.
const TABS = ["hours", "bands", "cover", "rules", "exceptions", "other"];
const NEW_HOURS = { days: [0, 1, 2, 3, 4, 5, 6], from: "10:30", to: "15:00" };
const NEW_BAND = { days: [0, 1, 2, 3, 4, 5, 6], from: "19:00", to: "22:30", capacity: 40 };
const today = () => new Date().toLocaleDateString("sv-SE");

const BookingSettings = () => {
  const { t } = useTranslation("bookings");
  const FULL = t("settings.daysFull", { returnObjects: true });
  const location = useLocation();
  const wanted = new URLSearchParams(location.search).get("tab") || (location.hash || "").slice(1);
  const [tab, setTab] = useState(TABS.includes(wanted) ? wanted : "hours");
  const [s, setS] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [special, setSpecial] = useState(null); // {closure}
  const [pauseOpen, setPauseOpen] = useState(false);

  useEffect(() => {
    if (TABS.includes(wanted)) setTab(wanted);
  }, [wanted]);

  const load = useCallback(async () => {
    try {
      const [st, sh, cl] = await Promise.all([GetBookingSettingsAPI(), GetShiftsAPI(), GetClosuresAPI()]);
      setS(st);
      setShifts(sortShifts(sh));
      setClosures(sortClosures(cl));
    } catch (e) {
      ErrorToaster(errText(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  // esegue un'operazione, mostra il messaggio verde e ricarica; true se è andata bene
  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) successToaster(ok);
      await load();
      return true;
    } catch (e) {
      ErrorToaster(errText(e));
      return false;
    }
  };

  if (!s) return null;
  const opening = s.opening_hours || [];
  const labels = { lunch: t("settings.lunch"), dinner: t("settings.dinner") };
  const bandLabel = (x) => bandName(x, shifts, labels);
  const shiftName = (id) => {
    const x = shifts.find((y) => y.id === id);
    return x ? bandLabel(x) : "";
  };
  const pause = s.pause && s.pause.active ? s.pause : null;
  const block = !s.enabled ? { kind: "off" } : clientBlock({ pause, closures, today: today() });
  const partialNote = pause && pause.shifts.length ? t("settings.previewPartial", { names: pause.shifts.map(shiftName).join(", ") }) : null;
  const closed = closedDays(opening);

  // Orari di apertura: si salva l'elenco intero
  const hoursItems = opening.map((o, i) => ({ ...o, key: String(i), index: i }));
  const saveHours = (rows) => run(() => SaveBookingSettingsAPI({ opening_hours: rows }), t("settings.openingSaved"));
  // Fasce di prenotazione = turni
  const bandItems = shifts.map((x) => ({
    key: x.id, id: x.id, days: x.weekdays, from: x.start_time, to: x.end_time || x.last_seating, capacity: x.capacity, raw: x,
    badge: x.active === false ? <Badge color="secondary" className="ml-2">{t("settings.inactive")}</Badge> : null,
  }));
  const bandBody = (r) => ({ weekdays: r.days, start_time: r.from, end_time: r.to, capacity: Number(r.capacity) });
  const bandWarn = (r) => outsideOpening({ weekdays: r.days, start_time: r.from, end_time: r.to }, opening);

  const tabTitle = {
    hours: t("settings.openingTitle"), bands: t("settings.bandsTitle"), cover: t("settings.coverTitle"),
    rules: t("settings.rules"), exceptions: t("settings.exceptionsTitle"), other: t("settings.otherTitle"),
  };

  return (
    <>
      <div className="mb-3">
        <h3 className="mb-1">{t("settings.pageTitle")}</h3>
        <p className="text-muted mb-0">{t("settings.subtitle")}</p>
      </div>

      {pause && (
        <div className="alert d-flex align-items-center justify-content-between flex-wrap" role="alert"
          style={{ background: "#fff1e0", border: "1px solid #f08c00", borderLeft: "5px solid #f08c00", color: "#5c3600" }}>
          <div className="mr-3">
            <strong>{t("settings.pauseActive")}</strong> · {pause.shifts.length ? pause.shifts.map(shiftName).join(", ") : t("settings.allServices")} ·{" "}
            {untilLabel(pause.until) === "00:00" ? t("settings.pauseUntilEod") : t("settings.pauseUntil", { time: untilLabel(pause.until) })}
            <div className="small">{pause.message || t("settings.previewDefaultPause")}</div>
          </div>
          <Button size="sm" style={{ background: "#f08c00", color: "#fff", border: 0 }} onClick={() => run(() => EndPauseAPI(), t("settings.pauseOff"))}>
            {t("settings.pauseEnd")}
          </Button>
        </div>
      )}

      <Row>
        <Col lg="8">
          <Nav pills className="mb-3 flex-wrap">
            {TABS.map((k) => (
              <NavItem key={k}>
                <NavLink href="#" active={tab === k} onClick={(e) => { e.preventDefault(); setTab(k); }} style={{ cursor: "pointer" }}>{tabTitle[k]}</NavLink>
              </NavItem>
            ))}
          </Nav>
          <Card>
            <CardBody>
              <TabContent activeTab={tab}>
                <TabPane tabId="hours">
                  <CardTitle tag="h4">{t("settings.openingTitle")}</CardTitle>
                  <HoursEditor
                    items={hoursItems} newRow={NEW_HOURS} addLabel={t("settings.addHours")} empty={t("settings.noOpening")}
                    onSave={(item, r) => {
                      const row = { days: r.days, from: r.from, to: r.to };
                      return saveHours(item ? opening.map((o, i) => (i === item.index ? row : o)) : [...opening, row]);
                    }}
                    onDelete={(item) => saveHours(opening.filter((_, i) => i !== item.index))}
                  />
                  <p className="small text-muted mt-3 mb-0">
                    {t("settings.closingDaysNote")}
                    {closed.length > 0 && <strong className="ml-1">{t("settings.closingDays", { days: dayStr(closed, FULL) })}</strong>}
                  </p>
                </TabPane>

                <TabPane tabId="bands">
                  <CardTitle tag="h4">{t("settings.bandsTitle")}</CardTitle>
                  <p className="text-muted small">{t("settings.bandsHelp")}</p>
                  <HoursEditor
                    withCap items={bandItems} newRow={NEW_BAND} addLabel={t("settings.addHours")} empty={t("settings.noBands")}
                    title={(it) => bandLabel(it.raw)} warn={bandWarn}
                    onSave={(item, r) =>
                      run(() => (item ? SaveShiftAPI(item.id, bandBody(r)) : AddShiftAPI(bandBody(r))), t("settings.bandSaved"))}
                    onDelete={(item) => run(() => DeleteShiftAPI(item.id), t("settings.bandDeleted"))}
                  />
                </TabPane>

                <TabPane tabId="cover">
                  <CardTitle tag="h4">{t("settings.coverTitle")}</CardTitle>
                  <CoverCard s={s} onSaved={setS} />
                </TabPane>

                <TabPane tabId="rules">
                  <CardTitle tag="h4">{t("settings.rules")}</CardTitle>
                  <RulesCard s={s} onSaved={setS} onPatch={(p) => setS((x) => ({ ...x, ...p }))} />
                </TabPane>

                <TabPane tabId="exceptions">
                  <CardTitle tag="h4">{t("settings.exceptionsTitle")}</CardTitle>
                  <p className="text-muted small">{t("settings.exceptionsHelp")}</p>
                  {closures.length === 0 && <p className="text-muted small">{t("settings.noExceptions")}</p>}
                  {closures.map((c) => (
                    <div key={c.id} className="d-flex align-items-center flex-wrap py-2 border-bottom">
                      <span className="flex-grow-1">
                        <strong>{c.reason}</strong> · {closureDates(c)} · {c.shift ? shiftName(c.shift) : t("settings.allServices")}
                        {c.message && <div className="small text-muted">{c.message}</div>}
                      </span>
                      <Badge color={c.is_open ? "success" : "danger"} className="mr-2">{t(c.is_open ? "settings.openState" : "settings.closedState")}</Badge>
                      <Button size="sm" color="link" title={t("settings.edit")} onClick={() => setSpecial({ closure: c })}>✎</Button>
                      <ConfirmDelete onConfirm={() => run(() => DeleteClosureAPI(c.id), t("settings.specialDeleted"))} />
                    </div>
                  ))}
                  <div className="mt-3">
                    <Button size="sm" color="link" className="px-0" onClick={() => setSpecial({ closure: null })}>{t("settings.specialAdd")}</Button>
                    <span className="mx-2 text-muted">·</span>
                    <Button size="sm" color="link" className="px-0" onClick={() => setPauseOpen(true)}>{t("settings.pauseTitle")}</Button>
                  </div>
                </TabPane>

                <TabPane tabId="other">
                  <CardTitle tag="h4">{t("settings.otherTitle")}</CardTitle>
                  <OtherCard s={s} onSaved={setS} />
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <div style={{ position: "sticky", top: 80 }}>
            <Card>
              <CardBody>
                <CardTitle tag="h5" className="text-center">{t("settings.previewTitle")}</CardTitle>
                <PhonePreview block={block} note={partialNote} />
                <small className="text-muted d-block text-center mt-3">{t(block ? "settings.previewBlocked" : "settings.previewOpen")}</small>
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>

      <SpecialDayModal isOpen={!!special} closure={special && special.closure} shifts={shifts} bandLabel={bandLabel}
        onClose={() => setSpecial(null)} onSaved={() => { setSpecial(null); load(); }} />
      <PauseModal isOpen={pauseOpen} shifts={shifts.filter((x) => x.active !== false)} bandLabel={bandLabel}
        onClose={() => setPauseOpen(false)} onSaved={() => { setPauseOpen(false); load(); }} />
    </>
  );
};

export default BookingSettings;
